import { InformationIcon } from '@va/icons';
import { useTranslate } from '@va/localization';
import { CustomDomainRecord } from '@va/shared/api/custom-domains';
import { isNil } from 'lodash';
import { useEffect, useMemo, useRef } from 'react';

export const useRefreshPendingDomain = ({
  pendingDomain,
  mutate,
}: {
  pendingDomain: CustomDomainRecord | undefined | null;
  mutate: () => void;
}) => {
  const pendingDomainPoolingInterval = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!pendingDomain) return;

    const interval = setInterval(() => {
      mutate();
    }, 3000);
    pendingDomainPoolingInterval.current = interval;

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDomain]);
};

export const useCustomDomainSaveButtonState = ({
  pendingDomain,
  isLoading,
  dirty,
}: {
  pendingDomain: CustomDomainRecord | null | undefined;
  isLoading: boolean;
  dirty: boolean;
}) => {
  const translate = useTranslate();
  const isSaveButtonDisabled = useMemo(() => {
    return !dirty || !isNil(pendingDomain) || isLoading;
  }, [dirty, isLoading, pendingDomain]);

  const saveButtonTooltip = useMemo(() => {
    if (!dirty) {
      return translate('labels.noChanges');
    }

    if (!isSaveButtonDisabled) {
      return (
        <div className='mt-3'>
          <InformationIcon className='inline' />
          {translate('standalone.customDomain.saveButtonTooltip')}
        </div>
      );
    }

    if (!isNil(pendingDomain)) {
      return translate('standalone.customDomain.errors.alreadyExistingPendingDomain');
    }
    return undefined;
  }, [dirty, isSaveButtonDisabled, pendingDomain, translate]);

  return { saveButtonTooltip, isSaveButtonDisabled };
};
